'use client';

import { useState } from 'react';
import { usePathname } from 'next/navigation';
import { ChevronDown, ExternalLink, HelpCircle, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { getHelpCentreTarget } from '@/lib/constants/help-centre';
import { trackGuidanceEvent } from '@/lib/guidance';
import { GUIDANCE_VIDEOS_FLAG, guidanceForAreaId } from '@/lib/guidance/areas';
import { useOptionalFeatureFlags } from '@/lib/providers/feature-flags-provider';
import { currentEnvironment } from '@/lib/config/environment';
import { GuidanceVideoDialog } from '@/components/shared/stage-help/guidance-video-dialog';

/**
 * Persistent Help Centre link (LF-5290) — rendered in the top-right corner
 * of every standard authenticated page's header. Deep-links to the article
 * or collection matching the current page (`lib/constants/help-centre.ts`),
 * falling back to the Help Centre home. Opens in a new tab.
 *
 * Its label names what it opens for the page, "How to use Discovery", read
 * from the matched mapping (VALE-2138); the home fallback says only "Help".
 * On a page with a guidance video it opens a menu of the video and the guide
 * instead, because the stage banner that also carries the video shows on a
 * user's first visit only, and this is where both live after that.
 *
 * The click is recorded (VALE-1790). It stays a plain anchor rather than becoming
 * a scripted navigation: middle-click, ⌘-click and "copy link address" all have
 * to keep working, and `onClick` fires for the ordinary left-click that is the
 * only one worth counting anyway. Telemetry is fire-and-forget, so nothing about
 * it can delay or block the new tab.
 */
export function HelpCentreButton() {
  const pathname = usePathname();
  const { key, url, label, areaId } = getHelpCentreTarget(pathname ?? '');
  const { hasFeature } = useOptionalFeatureFlags();
  const { video } = areaId
    ? guidanceForAreaId(areaId, {
        videosEnabled: hasFeature(GUIDANCE_VIDEOS_FLAG),
        production: currentEnvironment === 'production'
      })
    : {};
  const [videoOpen, setVideoOpen] = useState(false);

  const recordOpened = () =>
    trackGuidanceEvent('guidance-help-article-opened', {
      route: key,
      article: url
    });

  const face = (
    <>
      <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
      <span className="hidden sm:inline">{label}</span>
    </>
  );

  if (!video) {
    return (
      <Button
        density="compact"
        gap="standard"
        asChild
        variant="outline"
        size="xs"
      >
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          onClick={recordOpened}
        >
          {face}
        </a>
      </Button>
    );
  }

  return (
    <>
      {/* Non-modal: a modal menu that opens a dialog leaves the page inert
          after both close (LF-4072). */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            density="compact"
            gap="standard"
            variant="outline"
            size="xs"
            aria-label={label}
          >
            {face}
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setVideoOpen(true)}>
            <Play className="mr-2 h-4 w-4" aria-hidden="true" />
            Watch the video
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={recordOpened}
            >
              <ExternalLink className="mr-2 h-4 w-4" aria-hidden="true" />
              Read the guide
            </a>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <GuidanceVideoDialog
        videoId={video.id}
        title={label}
        open={videoOpen}
        onOpenChange={setVideoOpen}
      />
    </>
  );
}
